import axios from 'axios';
import * as Sentry from '@sentry/react';
import store from '../store';
import { jwtDecode } from 'jwt-decode';

// Helper function to get auth state from Redux store
function getAuthState() {
  const state = store.getState();
  return state.auth;
}

// Cache tenant ID per token to avoid repeated decoding
let cachedTenantId: string | null = null;
let cachedToken: string | null = null;

/**
 * Get tenant ID from JWT token
 * Returns the tenant ID number (e.g., "10001", "10002", etc.)
 * Uses caching to avoid repeated JWT decoding
 */
function getTenantIdFromToken(): string | null {
  const auth = getAuthState();
  const token = auth?.token;
  
  // If token is null (logout), clear cache and return null
  if (!token) {
    cachedToken = null;
    cachedTenantId = null;
    return null;
  }
  
  // Return cached value if token hasn't changed
  if (token === cachedToken && cachedTenantId !== null) {
    return cachedTenantId;
  }
  
  // Token changed - decode and cache new tenant ID
  try {
    const decoded: any = jwtDecode(token);
    
    // Try common field names for tenant ID in JWT token
    // Adjust based on your actual token structure
    const tenantId = decoded.TenantKey;
    
    if (!tenantId) {
      // Log available fields for debugging (only in dev)
      if (import.meta.env.DEV) {
        console.warn('Tenant ID not found in token. Available fields:', Object.keys(decoded));
      }
    }
    
    // Cache the result
    cachedToken = token;
    cachedTenantId = tenantId ? String(tenantId).trim() : null;
    
    return cachedTenantId;
  } catch (error) {
    console.error('Failed to decode token for tenant ID:', error);
    cachedToken = null;
    cachedTenantId = null;
    return null;
  }
}

/**
 * Clear the tenant ID cache (called when token changes)
 */
export function clearTenantIdCache() {
  cachedToken = null;
  cachedTenantId = null;
}

/**
 * Check if we're in dev or qa environment based on tenant name from auth state
 * Dev and qa use direct env variables (no tenant suffix)
 */
function isDevOrQaEnvironment(): boolean {
  const auth = getAuthState();
  const tenant = auth?.tenant?.toLowerCase() || '';
  return tenant === 'dev' || tenant === 'qa';
}


/**
 * Get the appropriate environment variable for inventory/legacy APIs based on tenant ID from token
 * For dev/qa: Uses direct env variables (VITE_INVENTORY_API_URL, VITE_LEGACY_API_URL)
 * For other environments: Builds tenant-specific env variable name like: VITE_INVENTORY_API_URL_10001
 * Only returns URLs - no fallback to defaults
 */
function getEnvVariableForTenant(baseEnvKey: string): string {
  // Dev and qa use direct env variables (no tenant suffix)
  if (isDevOrQaEnvironment()) {
    const auth = getAuthState();
    const directValue = import.meta.env[baseEnvKey];
    if (!directValue) {
      console.error(`❌ Environment variable not found: ${baseEnvKey}. Tenant: ${auth?.tenant || 'unknown'}`);
    }
    return directValue || '';
  }
  
  // Production and other environments use tenant-specific URLs
  const tenantId = getTenantIdFromToken();
  
  // If no tenant ID, return empty (no default fallback)
  if (!tenantId) {
    if (import.meta.env.DEV) {
      console.warn(`⚠️ No tenant ID found in token, cannot determine ${baseEnvKey}`);
    }
    return '';
  }
  
  // Build tenant-specific env variable name: VITE_INVENTORY_API_URL_10001
  const tenantEnvKey = `${baseEnvKey}_${tenantId}`;
  
  // Get tenant-specific env variable
  const tenantSpecificValue = import.meta.env[tenantEnvKey];
  
  if (!tenantSpecificValue) {
    console.error(`❌ Tenant-specific env variable not found: ${tenantEnvKey}. Tenant ID: ${tenantId}`);
  }
  
  return tenantSpecificValue || '';
}

// Main API instance - set directly from VITE_API_URL (same for all tenants, doesn't need tenant-specific logic)
export const mainApiInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Inventory API instance
export const inboundApiInstance = axios.create({
  baseURL: '',
  headers: {
    'Content-Type': 'application/json',
  },
});

export const legacyApiInstance = axios.create({
  baseURL: '',
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Update axios instance baseURLs dynamically based on current tenant ID from token
 * Exported so it can be called from Redux listener middleware
 * Called in interceptors to ensure URLs are always current
 */
export function updateBaseURLsFromTenant() {
  // Note: mainApiInstance.baseURL is set directly from VITE_API_URL when instance is created
  // (no need to update it dynamically since it's the same for all tenants)
  
  // Inventory and Legacy APIs use tenant-specific logic
  const inventoryUrl = getEnvVariableForTenant('VITE_INVENTORY_API_URL');
  const legacyUrl = getEnvVariableForTenant('VITE_LEGACY_API_URL');
  
  // Only update inventory/legacy if URLs exist - no fallback to defaults
  if (inventoryUrl) inboundApiInstance.defaults.baseURL = inventoryUrl;
  if (legacyUrl) legacyApiInstance.defaults.baseURL = legacyUrl;
  
  // If logged out or no tenant ID, clear inventory/legacy URLs
  const tenantId = getTenantIdFromToken();
  const auth = getAuthState();
  
  // For inventory/legacy, clear if no tenant ID (since they need tenant-specific URLs in prod)
  if (!tenantId && !isDevOrQaEnvironment()) {
    inboundApiInstance.defaults.baseURL = '';
    legacyApiInstance.defaults.baseURL = '';
  }
  
  // If logged out completely, clear inventory/legacy
  if (!auth?.token || !auth?.tenant) {
    inboundApiInstance.defaults.baseURL = '';
    legacyApiInstance.defaults.baseURL = '';
  }
}

// Initialize URLs immediately if auth state is already available
// This handles the case where a user is already logged in when code updates/reloads
// REHYDRATE listener will also call this, but this ensures URLs are set ASAP
try {
  const initialState = store.getState();
  if (initialState?.auth?.token && initialState?.auth?.tenant) {
    updateBaseURLsFromTenant();
  }
} catch (error) {
  // Store might not be fully initialized yet - that's okay,
  // REHYDRATE listener will handle it
  if (import.meta.env.DEV) {
    console.log('⚠️ Could not initialize URLs immediately (store not ready), will wait for REHYDRATE');
  }
}

// Add interceptors to both instances
const addInterceptors = (instance: typeof mainApiInstance, instanceName: string) => {
  // Request interceptor
  instance.interceptors.request.use((config) => {
    // For inventory/legacy instances (not main), ensure baseURL is set before request
    // This handles edge cases where requests happen before REHYDRATE completes
    // Main API doesn't need this check since its baseURL is set directly from VITE_API_URL
    const isInventoryOrLegacy = instanceName === 'inventory' || instanceName === 'legacy';
    if (isInventoryOrLegacy && !instance.defaults.baseURL) {
      // Try to update URLs if auth state is available (lazy initialization)
      updateBaseURLsFromTenant();
    }
    
    // Update this request's baseURL if it wasn't explicitly set
    // (The middleware already updated instance.defaults.baseURL when token changed)
    if (!config.baseURL) {
      config.baseURL = instance.defaults.baseURL;
    }
    
    // Add Sentry breadcrumb for request
    Sentry.addBreadcrumb({
      message: `API Request: ${config.method?.toUpperCase()} ${config.url}`,
      category: 'http',
      level: 'info',
      data: {
        instance: instanceName,
        method: config.method,
        url: config.url,
        baseURL: config.baseURL,
        headers: config.headers,
        params: config.params,
        data: config.data, // Include request payload
      },
    });
    
    return config;
  });

  // Response interceptor
  instance.interceptors.response.use(
    (response) => {
      // Add Sentry breadcrumb for successful response
      Sentry.addBreadcrumb({
        message: `API Success: ${response.status} ${response.config.url}`,
        category: 'http',
        level: 'info',
        data: {
          instance: instanceName,
          status: response.status,
          url: response.config.url,
        },
      });
      
      return response;
    },
    (error) => {
      // Get user info from Redux
      const auth = getAuthState();
      
      // Set user context if available
      if (auth?.userId || auth?.userName) {
        Sentry.setUser({
          id: auth.userId || '',
          email: auth.userName || '',
          username: auth.userName || '',
        });
      }
      
      // Add Sentry breadcrumb for error
      Sentry.addBreadcrumb({
        message: `API Error: ${error.response?.status || 'Network Error'} ${error.config?.url}`,
        category: 'http',
        level: 'error',
        data: {
          instance: instanceName,
          status: error.response?.status,
          url: error.config?.url,
          error: error.message,
        },
      });
      
      // Capture the error in Sentry with standardized tags
      Sentry.captureException(error, {
        tags: {
          context: 'main_thread',                                    // Required - context tag
          api_instance: instanceName,                                 // Required for API errors
          http_method: error.config?.method?.toUpperCase() || 'UNKNOWN', // HTTP method
          http_status: error.response?.status,                       // HTTP status (if response error)
          user_email: auth?.userName || '',                           // User email for filtering
          tenant: auth?.tenant || '',                                 // Tenant for filtering
          error_type: error.response ? 'api' : 'network',           // Error category
        },
        extra: {
          api_url: error.config?.url,                                // Full URL in extra (not tag)
          response_data: error.response?.data,
          request_config: error.config,
          request_payload: error.config?.data,                        // Include request payload in error context
          request_params: error.config?.params,                       // Include query parameters
          request_headers: error.config?.headers,                     // Include request headers
        },
      });

      // Your existing error handling
      let errorMessage = 'An error occurred';

      if (error.response) {
        console.error('Response Error:', {
          status: error.response.status,
          message: error.response.data,
        });
        errorMessage = error.response.data || error.message;
      } else if (error.request) {
        console.error('Request Error: No response received', error.request);
        errorMessage = 'No response from the server';
      } else {
        console.error('General Error:', error.message);
        errorMessage = error.message;
      }

      return Promise.reject({ ...error, message: errorMessage });
    }
  );
};

addInterceptors(mainApiInstance, 'main');
addInterceptors(inboundApiInstance, 'inventory');
addInterceptors(legacyApiInstance, 'legacy');

// Keep the default export for backward compatibility
export default mainApiInstance;
